<template>
  <div class="autocompleterContainer">
    
    <ClickPopup
      ref="toggler"
      class="dropdown"
      :options="{placement: 'bottom-start'}"
      v-model:show="isOpen"
      :toBody="toBody"
      >
      <template v-slot:trigger>
          <button class="unfancyButton toggleDropDown" :disabled="disabled" ref="toggleDropDown" type="button">{{ labelSelected() }}</button>
          <button v-if="selected" class="unfancyButton clear" :disabled="disabled" @click.prevent.stop="selected = null" type="button">
            <span class="autocompleter-clearselected"></span>
          </button>
          <span v-else class="autocompleter-angledown clear"></span>
      </template>
      <template v-slot:content>
        <div class="dropdown">
          <input ref="searchInput" v-model="searchValue" @keydown.enter.prevent="selectIndex" @keydown.down.prevent="focusDown" @keydown.up.prevent="focusUp" /><span v-if="request.isRequesting.value">Lädt</span>
          <div class="options">
            <div v-if="minimalSearchStringLength && minimalSearchStringLength > 0 && searchValue?.length < minimalSearchStringLength">
            mind. {{ minimalSearchStringLength }} Zeichen eingeben, um zu suchen
          </div>
            <div class="option" :class="{ highlight: index === highlightedIndex }" 
              v-for="(option, index) in options" 
              @click="select(option)" 
              @mouseenter="highlightedIndex = index"
              v-html="highlightedLabel(option)">
            </div>
            <div v-if="!options.length && !request.isRequesting.value && searchValue && searchValue?.length >= minimalSearchStringLength">
            kein Ergebnis
          </div>
          </div>
        </div>
      </template>
    </ClickPopup>
  </div>
</template>

<script>
/**
  @file erzeugt eine Combobox, die eine Liste anbietet, die über Requests gefiltert wird.
  Schreibt das ausgewählte Ergebnis
  Nutzung:
     <AutocompleterLookup 
        template="MIP_MASSNAHME"  // Name der abzufragenden XMLRDB-Klasse
        val="selectedValue"       // Ausgewähltes Objekt 
        style="width: 260px; margin-right:5px;"/>
  Alternative Einsatzmöglichkeiten:
  - r2x: XMLRDB-Templategenerator. Default: R2XLookup
         Falls ein anderer XMLRDB-Templategenerator genutzt wird, muss auch der template-Parameter angepasst werden
  - id: Ausgewählte Objekt-ID (Alternativ/ergänzend zu selected)
  - requestCount: Anzahl der Elemente in der Liste
  - placeholder: placeholder im Eingabefeld
  - filter: XMLRDB--JSON-Filter. Dieser wird zusätzlich zur Suchabfrage auf die abzufragende Liste angewendet. Er kann nicht vom Suchbegriff abhängig sein.
     Einsatzbeispiel: Es sollen nur Gemeinden im Kreis "4711" auswählbar sein:
        filter = {
           col: "KREIS_NR, 
           op: "eq",
           value: "4711"
        }
     Der Filter darf auch ein leeres Objekt sein. In deisem Fall wird er nicht angewendet
     Es ist also sinnvoll, das filterobjekt während der Bearbeitung zu verändern.
  - filterFunc: Ähnlich wie "filter". Aber: Man übergibt keinen Filter, sondern eine *Funktion*, die einen Filter zurückliefert.
    Frage: Warum braucht man sowohl filter als auch filterFunc?
    Antwort1: Die filterFunc-Funktion bekommt als Parameter das Lookupattribut UND den aktuellen Suchbegriff. 
             Der "filter" kann nicht vom Suchbegriff abhängig sein. :
    Antwort2: filterFunc ist historisch nach filter entstanden. Wir hätten direkt eine filterFunc machen sollen ...
        Attribute.AjaxLookupFilterFunction["Register.APPL_ORGANISATION.GEMEINDE_NR"] = (attr,search) => {
          const theFilter = {
            op:"locate",
            col:"NAME",
            value:search
          };
          return theFilter;
        }
 */
import { watch, watchEffect, shallowRef, shallowReactive, onMounted, onUnmounted, nextTick } from "vue";
import { fetchXML2Metadata }  from "../../xmlrdb-metadata/XML2Metadata";
import ClickPopup from "./ClickPopup.vue";
import SingleFetch from "../../utils/SingleFetch";
import scrollIntoView from "scroll-into-view-if-needed";


export default {
  name: "AutocompleterLookup",
  props: {
    val: Object, // Ausgewähltes Objekt
    id: Object,  // Ausgewählte Objekt-ID (Alternativ/ergänzend zu selected)
    template: String,
    element: String, // Name der zu suchenden Elemente. Default: element = template
    r2x: {
      type: String,
      default: "R2XLookup"
    },
    requestCount: {
      type: Number,
      default: 200
    },
    placeholder: {
      type: String,
      default: "ungesetzt"
    }, 
    filter: {
       type: Object,
       default: null
    },
    filterFunc: {
       type: Function,
       default: null
    },
    disabled: Boolean,
    ignoreLookupActive: {
      type: Boolean,
      default: false
    },
    minimalSearchStringLength: {
      type: Number,
      default: 0
    },
    queryParams: {
      type: String
    },
    toBody: { type: Boolean, default: undefined }
  },
  components: {
    ClickPopup
  },
  setup: function(props, {emit}){
    const selected = shallowRef(props.val?.value);
    const searchValue = shallowRef("");
    const options = shallowReactive([]);
    const isOpen = shallowRef(false);
    const searchInput = shallowRef();
    const dropdown = shallowRef();
    const toggleDropDown = shallowRef();
    const highlightedIndex = shallowRef(-1);
    const toggler = shallowRef();

    if(props.val){
      watch(props.val, function(){
        selected.value = props.val?.value;
      });
    }

    if(props.id){
      watch(props.id, ()=>{
        if(props.id?.value === null){
          selected.value = null;
        }
      });
    }

    let request = new SingleFetch();

    const meta = shallowRef();
    watchEffect(async function(){
      // Diese Anweisung ist in watchEffect, weil props.r2x und ein reactive sind und sich daher theoretisch ändern könnten.   
      const md = await fetchXML2Metadata(props.r2x,props.template);
      meta.value = md.getElement("1");
    });

    function fetchAuswahl() {
      let filter = { and: [] };
      if (!props.ignoreLookupActive) {
        meta.value.allAttributes.forEach(a => {
          if (a.tagValueByName("isLookupActiveIndicator") === "true") {
            filter.and.push({
              col: a.name(),
              op: "ne",
              value: 0
            })
          }
        })
      }
      let params = "";
      if(props.queryParams){
        params += "&" + props.queryParams;
      }
      const searchText = searchValue.value ?? "";
      // Suchtext auswerten, falls gesetzt
      if (searchText.length>0) {
        filter.and.push({
            or: meta.value.allAttributes.filter( a => a.isLookupLabel()).map(a =>{ return {col: a.name(), op: "locate", value: searchText}})
        });
      }       
      // Zusätzlichen Filter anwenden? Nur wenn, der vorhanden und nicht leer ist
      if (props.filter && Object.keys(props.filter).length>0) {
        filter.and.push(props.filter);
      }
      // Filter einsetzen, der (über eine Funktion) auch den searchValue nutzen will?
      if (props.filterFunc ) {
        const filterByFunc = props.filterFunc(searchText);
        // Filter nur anwenden, wenn er nicht leer ist
        if ( filterByFunc && Object.keys(filterByFunc).length>0)  {
          filter.and.push(filterByFunc);
        }
      }
      console.log(searchText,searchText.length,props.minimalSearchStringLength);
      if (searchText.length >= props.minimalSearchStringLength) {
        request.fetch( `xmlrdbJSON/read/${props.r2x}/${props.template}?~META=0&:.elementcount=${props.requestCount}${params}`,{
          body: JSON.stringify( { filter }),
          method: "POST"
        }).then(e => e.json()).then(e => {
          options.splice(0, options.length, ...e[props.element || props.template]);
        });
      }
      else {
        options.splice(0, options.length);
      }
    }
    
    watch(searchValue, ()=>{
      highlightedIndex.value = -1;
      if(meta.value){
         fetchAuswahl();
      }
    });

    watch(selected, ()=>{
      if(props.val){
        props.val.value = selected.value;
      }
      if (props.id) {
        if ( selected.value  && meta.value) {
          let attr = meta.value.getPrimaryKeyAttributes()[0];
          props.id.value = selected.value[attr.name()];
        }
        else {
          props.id.value = null;
        }
      }

      emit("value", selected.value);
      let id = null;
      if(meta.value && selected.value){
        let attr = meta.value.getPrimaryKeyAttributes()[0];
        id = selected.value[attr.name()];
      }
      emit("id", id);
    });

    function select(data){
      selected.value = data;
      isOpen.value = false;
    }

    function selectIndex(){
      if(options){
        if(highlightedIndex.value === -1){
          select(options[0]);
        }
        else{
          select(options[highlightedIndex.value]);
        }
      }
      isOpen.value = false;
    }

    function label(data){
      if(data){
        return meta.value?.getLookupLabel(data) || props.placeholder;
      }
      else{
        return props.placeholder;
      }
    }

    function labelSelected() {
      return label(selected.value);
    } 

    function highlightedLabel(data){
      if(searchValue.value)
        return label(data).replaceAll(searchValue.value, `<b>${searchValue.value}</b>`)
      else
        return label(data);
    }

    function focusUp(){
      if(highlightedIndex.value !== -1)
        highlightedIndex.value = Math.max(0, highlightedIndex.value - 1 );
      scrollIntoView(toggler.value.popperContent.popup, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    function focusDown(){
      highlightedIndex.value = Math.min(options.length - 1, highlightedIndex.value + 1 );
      scrollIntoView(toggler.value.popperContent.popup, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    onMounted(()=>{
      watch(isOpen, ()=>{
        if(isOpen.value){
          fetchAuswahl();
          nextTick(()=>{
            if(isOpen.value)
              searchInput.value?.focus();
          });
        }
      })
    })
    

    return {
      options,
      label,labelSelected, highlightedLabel,
      searchValue,
      searchInput,
      dropdown,
      select,
      selected,
      selectIndex,
      isOpen,
      highlightedIndex,
      focusUp,
      focusDown,
      toggleDropDown,
      toggler,
      request
    }
  }
}
</script>

<style scoped="true">

  .options{
    max-height: 200px;
    overflow: auto;
    overscroll-behavior: contain;
  }

  .option{
    margin: 0;
    padding: 3px 1em 3px .4em;
    cursor: pointer;
    min-height: 0;
  }

  .option.highlight{
    background: lightgrey;
  }

  .dropdown {
    background-color: white;
    user-select: none;
    min-width: 200px;
    box-sizing: border-box;
    z-index: 10000;
  }

  .dropdown > input{
    width:100%;
    box-sizing: border-box;
  }

  .toggleDropDown{
    cursor: pointer;
    display:inline-block;
    min-width: 1em;
    padding: 2px 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
  }

  .clear {
    cursor: pointer;
    font-size: 1.3em;
    padding: 0 0.3em;
  }

  .autocompleterContainer:deep(.trigger){
    display: inline-flex;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    background-color: #fff;
  }

  .autocompleterContainer{
    display: inline-block;
  }

  .unfancyButton{
    border: 0;
    margin:0;
    background: 0;
    cursor: pointer;
    text-align: left;
  }

</style>